/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  type UseQueryOptions,
  type QueryKey
} from "react-query";
import {
  bulkImportUsers,
  createBadgeDefinition,
  createInstanceUser,
  createIntegrationConfig,
  createIntegrationToken,
  createScheduler,
  deleteAccessAttempts,
  deleteBadgeDefinition,
  deleteIntegrationToken,
  deleteScheduler,
  getAdminSettingsActivityLogs,
  getAllAccessAttempts,
  getAllSchedules,
  getBadgeDefinitions,
  getBranding,
  getConstanceConfigs,
  getInstanceSettingBusinessUnits,
  getInstanceSettingRegions,
  getInstanceSettingUserGroups,
  getInstanceUsers,
  getIntegrationConfigs,
  getIntegrationToken,
  getIntegrationTokens,
  getTaskLogs,
  setBranding,
  setWfDescIcon,
  updateBadgeDefinition,
  updateConstanceConfig,
  updateInstanceUser,
  updateScheduler,
  updateIntegrationConfig,
  updateIntegrationToken
} from "../services/instanceSettings.services";
import { paginatedParams, queryClient } from "@certa/queries/utils/utils";
import type {
  AdminSettingsActivityLog,
  AdminSettingsActivityLogFilters,
  BadgeDefinition,
  ConstanceConfigs,
  InstanceSettingsBusinessUnit,
  InstanceSettingsRegion,
  InstanceSettingsUserGroup,
  IntegrationConfig,
  IntegrationTokensListItem,
  UserConfig,
  UsersParams
} from "../types/instanceSettings.types";
import { useGetAllPages } from "./utility.hook";
import type { PaginatedResultsResponse } from "../types/generic.types";
import type {
  UseInfiniteQueryExtraConfigType,
  UseMutationExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";
import type { Badge } from "@certa/types";
import { useCheckPermission } from "@certa/common/utils/permissions/Chowkidar";
import { BADGE_PERMISSIONS } from "@certa/common/constants/badges";

const BRANDING_KEY = "instanceSettingsBranding";
const INTEGRATION_TOKEN_KEY = "instanceSettingsIntegrationTokens";
const INTEGRATION_CONFIG_KEY = "instanceSettingsIntegrationConfigs";
const INSTANCE_USER_GROUPS = "instance_user_groups";
const INSTANCE_BU = "instance_bu";
const INSTANCE_REGIONS = "instance_regions";
const USER_CONFIGS = "instanceSettingsUserConfigs";
const ACTIVITY_LOGS = "instanceSettingsActivityLogs";
const CONSTANCE_CONFIGS = "constance_configs";
const ACCESS_ATTEMPTS = "access-attempts";
const TASK_LOGS = "task-logs";
const BADGE_DEFINITIONS = "badge-definitions";
const SCHEDULES = "schedules";

// Branding ///////////////////////////////////////////////////////////////////
export const useBrandingSettings = () =>
  useQuery({
    retry: 0,
    cacheTime: Infinity,
    staleTime: Infinity,
    queryKey: [BRANDING_KEY],
    queryFn: getBranding
  });

const getBrandingSettingsFromQC = async () => {
  await queryClient.prefetchQuery([BRANDING_KEY], getBranding, {
    staleTime: Infinity,
    cacheTime: Infinity
  });
  return queryClient.getQueryData([BRANDING_KEY]);
};

export const useUpdateBrandingSettings = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(setBranding, extraConfig);

export const useUpdateWfDescIcon = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(setWfDescIcon, extraConfig);

// Integration Tokens /////////////////////////////////////////////////////////
export const useIntegrationTokens = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getIntegrationTokens>>
) => {
  return useQuery<IntegrationTokensListItem[]>({
    refetchOnWindowFocus: false,
    retry: 0,
    cacheTime: Infinity,
    staleTime: Infinity,
    ...config,
    queryKey: [INTEGRATION_TOKEN_KEY],
    queryFn: () => getIntegrationTokens()
  });
};

export const useIntegrationTokenDetails = (
  id?: number,
  config?: UseQueryOptions<UseQueryReturnType<typeof getIntegrationToken>>
) => {
  return useQuery({
    refetchOnWindowFocus: true,
    queryKey: "integration-token-details" as QueryKey, // TODO: Confirm why are we not using the id as well?
    ...config,
    enabled: !!id,
    queryFn: () => {
      if (!id) {
        return Promise.reject("Integration token id is required");
      }

      return getIntegrationToken(id);
    }
  });
};

export const useCreateIntegrationToken = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createIntegrationToken, extraConfig);

export const useUpdateIntegrationToken = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateIntegrationToken, extraConfig);

export const useDeleteIntegrationToken = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deleteIntegrationToken, extraConfig);

// Integration Config /////////////////////////////////////////////////////////
export const useIntegrationConfigs = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getIntegrationConfigs>>
) => {
  return useQuery<IntegrationConfig[]>({
    refetchOnWindowFocus: false,
    retry: 0,
    cacheTime: Infinity,
    staleTime: Infinity,
    ...config,
    queryKey: [INTEGRATION_CONFIG_KEY],
    queryFn: () => getIntegrationConfigs()
  });
};

export const useGetIntegrationTokensMutation = (
  config?: UseMutationExtraConfigType
) => useMutation<IntegrationTokensListItem[]>(getIntegrationTokens, config);

export const useGetIntegrationConfigsMutation = (
  config?: UseMutationExtraConfigType
) => useMutation<IntegrationConfig[]>(getIntegrationConfigs, config);

const getIntegrationConfigsFromQC = async () => {
  await queryClient.prefetchQuery(
    [INTEGRATION_CONFIG_KEY],
    getIntegrationConfigs,
    {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  );
  return queryClient.getQueryData([INTEGRATION_CONFIG_KEY]);
};

export const useCreateIntegrationConfig = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createIntegrationConfig, extraConfig);

export const useUpdateIntegrationConfig = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateIntegrationConfig, extraConfig);

// User Groups ///////////////////////////////////////////////////////////////
const useInstanceUserGroupsPaginated = (
  config?: UseInfiniteQueryExtraConfigType
) =>
  useInfiniteQuery<PaginatedResultsResponse<InstanceSettingsUserGroup>>(
    [INSTANCE_USER_GROUPS],
    async ({ pageParam = 0 }) => {
      return getInstanceSettingUserGroups(pageParam);
    },
    {
      ...paginatedParams,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: false,
      ...config
    }
  );

export const useInstanceUserGroups = (
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useInstanceUserGroupsPaginated(config);
  return useGetAllPages<InstanceSettingsUserGroup>(queryResult);
};

// Instance Business Units ///////////////////////////////////////////////////////////////
export const useInstanceBusinessUnits = (
  config?: UseQueryOptions<
    UseQueryReturnType<typeof getInstanceSettingBusinessUnits>
  >
) => {
  return useQuery<InstanceSettingsBusinessUnit[]>({
    refetchOnWindowFocus: false,
    retry: 0,
    cacheTime: Infinity,
    staleTime: Infinity,
    ...config,
    queryKey: [INSTANCE_BU],
    queryFn: () => getInstanceSettingBusinessUnits()
  });
};

// Instance Regions ///////////////////////////////////////////////////////////////
export const useInstanceRegions = (
  config?: UseQueryOptions<UseQueryReturnType<typeof getInstanceSettingRegions>>
) => {
  return useQuery<InstanceSettingsRegion[]>({
    refetchOnWindowFocus: false,
    retry: 0,
    cacheTime: Infinity,
    staleTime: Infinity,
    ...config,
    queryKey: [INSTANCE_REGIONS],
    queryFn: () => getInstanceSettingRegions()
  });
};

// Instance User Config ///////////////////////////////////////////////////////////////
// Needs updates to hooks for correct Typing.
const useInstanceSettingUserConfig = <
  T = PaginatedResultsResponse<UserConfig> | UserConfig[]
>(
  queryParams: UsersParams,
  config?: UseQueryOptions<T, Error>
) =>
  useQuery({
    refetchOnWindowFocus: false,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    ...config,
    queryKey: [USER_CONFIGS, queryParams] as QueryKey,
    queryFn: async () => getInstanceUsers(queryParams) as unknown as T
  });

// Get user configs in paginated way
export const useUserConfigsPaginated = (
  queryParams: UsersParams,
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useInstanceSettingUserConfig<
    PaginatedResultsResponse<UserConfig>
  >(queryParams, config);
  return queryResult;
};

/**
 * Get all user configs at once. Here have passed enabled: false to disable the query on mount rather to use it on demand
 */
export const useAllUserConfigs = (
  queryParams: UsersParams,
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useInstanceSettingUserConfig<UserConfig[]>(queryParams, {
    enabled: false,
    ...config
  });
  return queryResult;
};

export const useUpdateUserConfig = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(updateInstanceUser, extraConfig);

export const useCreateUserConfig = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(createInstanceUser, extraConfig);

// Instance Settings Activity Logs or Admin Settings Activity Logs //////////////////////////////

const useAdminSettingsActivityLogsPaginated = (
  queryParams?: AdminSettingsActivityLogFilters,
  config?: UseInfiniteQueryExtraConfigType
) =>
  useInfiniteQuery<PaginatedResultsResponse<AdminSettingsActivityLog>>(
    [ACTIVITY_LOGS],
    async ({ pageParam = 0 }) => {
      return getAdminSettingsActivityLogs(pageParam, queryParams);
    },
    {
      ...paginatedParams,
      refetchOnWindowFocus: false,
      retry: 0,
      cacheTime: Infinity,
      ...config
    }
  );
export const useAdminSettingsActivityLogs = (
  queryParams?: AdminSettingsActivityLogFilters,
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useAdminSettingsActivityLogsPaginated(
    queryParams,
    config
  );
  return useGetAllPages<AdminSettingsActivityLog>(queryResult);
};

export const useConstanceConfigs = () => {
  return useQuery<ConstanceConfigs>({
    refetchOnMount: true,
    queryKey: CONSTANCE_CONFIGS,
    queryFn: getConstanceConfigs
  });
};

const getConstanceConfigsFromQC = async () => {
  await queryClient.prefetchQuery([CONSTANCE_CONFIGS], getConstanceConfigs, {
    staleTime: Infinity,
    cacheTime: Infinity
  });
  return queryClient.getQueryData([CONSTANCE_CONFIGS]);
};

export const useUpdateConstanceConfig = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateConstanceConfig, extraConfig);

export const useInstanceSettingsInvalidateQueries = () => {
  const invalidateConstanceConfig = (): Promise<void> =>
    queryClient.invalidateQueries([CONSTANCE_CONFIGS]);

  const invalidateActivityLogs = (): Promise<void> =>
    queryClient.invalidateQueries([ACTIVITY_LOGS]);

  return {
    invalidateConstanceConfig,
    invalidateActivityLogs
  };
};

export const useGetAllAccessAttempts = () => {
  return useQuery({
    refetchOnMount: true,
    queryKey: ACCESS_ATTEMPTS,
    queryFn: getAllAccessAttempts
  });
};

const getAccessAttemptsFromQC = async () => {
  await queryClient.prefetchQuery([ACCESS_ATTEMPTS], getAllAccessAttempts, {
    staleTime: Infinity,
    cacheTime: Infinity
  });
  return queryClient.getQueryData([ACCESS_ATTEMPTS]);
};

export const useDeleteAccessAttemps = (config?: UseMutationExtraConfigType) =>
  useMutation(deleteAccessAttempts, config);

export const useUserBulkImportTaskLogs = (payload: {
  statuses: string;
  taskLogType: string;
}) =>
  useQuery({
    refetchOnMount: true,
    queryKey: TASK_LOGS,
    queryFn: () => getTaskLogs(payload)
  });

export const useBulkImportUsers = (config?: UseMutationExtraConfigType) =>
  useMutation(bulkImportUsers, config);

// Badge Definitions ///////////////////////////////////////////////////////////////

type UseBadgeDefinitionsConfig = UseInfiniteQueryExtraConfigType & {
  contentType?: "workflow" | "object";
  typeTag?: string;
};

const useBadgeDefinitionsPaginated = (config?: UseBadgeDefinitionsConfig) => {
  const { contentType, typeTag, ...queryConfig } = config || {};

  return useInfiniteQuery<PaginatedResultsResponse<BadgeDefinition>>(
    [BADGE_DEFINITIONS, contentType, typeTag],
    async ({ pageParam = 0 }) => {
      return getBadgeDefinitions(pageParam, contentType, typeTag);
    },
    {
      ...paginatedParams,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: false,
      ...queryConfig
    }
  );
};

export const useBadgeDefinitions = (config?: UseBadgeDefinitionsConfig) => {
  const hasPermissionsToSeeBadges = useCheckPermission(
    BADGE_PERMISSIONS.can_view_badge.codename
  );

  const { enabled: isEnabled, ...restConfig } = config || {};
  const queryResult = useBadgeDefinitionsPaginated({
    ...restConfig,
    enabled:
      isEnabled !== undefined
        ? isEnabled && hasPermissionsToSeeBadges
        : hasPermissionsToSeeBadges
  });
  return useGetAllPages<BadgeDefinition>(queryResult);
};

export const useCreateBadgeDefinition = (config?: UseMutationExtraConfigType) =>
  useMutation(createBadgeDefinition, config);

export const useUpdateBadgeDefinition = (config?: UseMutationExtraConfigType) =>
  useMutation(updateBadgeDefinition, config);

export const useDeleteBadgeDefinition = (config?: UseMutationExtraConfigType) =>
  useMutation(deleteBadgeDefinition, config);

export const convertBadgeDefinitionsToBadges = (
  badgeDefinitions: BadgeDefinition[]
): Badge[] => {
  return badgeDefinitions.map(badge => ({
    fieldTag: badge.fieldTag ?? null,
    name: badge.name,
    helpText: badge.helpText,
    tag: badge.tag,
    uiSpecs: badge.uiSpecs
  }));
};

// Scheduler ///////////////////////////////////////////////////////////////////

export const useGetAllSchedules = () => {
  return useQuery({
    refetchOnMount: true,
    queryKey: SCHEDULES,
    queryFn: getAllSchedules
  });
};

export const useUpdateScheduler = (config?: UseMutationExtraConfigType) =>
  useMutation(updateScheduler, config);

export const useCreateScheduler = (config?: UseMutationExtraConfigType) =>
  useMutation(createScheduler, config);

export const useDeleteScheduler = (config?: UseMutationExtraConfigType) =>
  useMutation(deleteScheduler, config);
